import { useEffect, useRef } from 'preact/hooks';
import { state, copyWeek } from '../store/store';
import { showToast } from '../store/toast';
import { formatWeekRange } from '../lib/dates';

export function CopyWeekModal(props: {
  open: boolean;
  currentWeekId: string;
  onClose: () => void;
}) {
  const s = state.value!;
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousActiveRef = useRef<HTMLElement | null>(null);

  // Escape closes
  useEffect(() => {
    if (!props.open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') props.onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props.open]);

  // Scroll-lock, focus first element, restore focus on close
  useEffect(() => {
    if (!props.open) return;
    previousActiveRef.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    queueMicrotask(() => {
      const focusable = dialogRef.current?.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    });
    return () => {
      document.body.style.overflow = prevOverflow;
      previousActiveRef.current?.focus();
    };
  }, [props.open]);

  function trapTab(e: KeyboardEvent) {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusables = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    );
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  if (!props.open) return null;

  const priorWeeks = s.weeks
    .filter(w => w.id !== props.currentWeekId && w.meals.some(m => m.recipe_id))
    .slice()
    .sort((a, b) => b.start_date.localeCompare(a.start_date));

  async function handleCopy(sourceWeekId: string, label: string) {
    const { copied, skipped } = await copyWeek(sourceWeekId, props.currentWeekId);
    props.onClose();
    let msg = `Copied ${copied} meal${copied === 1 ? '' : 's'} from ${label}`;
    if (skipped > 0) msg += ` (${skipped} slot${skipped === 1 ? '' : 's'} already had meals, skipped)`;
    showToast(msg);
  }

  return (
    <div className="add-to-plan-modal" onClick={() => props.onClose()}>
      <div
        ref={dialogRef}
        className="add-to-plan-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="copyweek-title"
        onKeyDown={trapTab}
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 480 }}
      >
        <div className="add-to-plan-header">
          <strong id="copyweek-title" style={{ fontSize: 16 }}>Copy from another week</strong>
          <button
            type="button"
            aria-label="Close"
            onClick={() => props.onClose()}
            className="add-to-plan-close"
          >×</button>
        </div>

        {priorWeeks.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: 14 }}>
            No prior weeks with planned meals yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {priorWeeks.map(w => {
              const mealCount = w.meals.filter(m => m.recipe_id).length;
              const range = formatWeekRange(w.start_date);
              return (
                <div key={w.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px', background: 'var(--surface-card)',
                  borderRadius: 'var(--radius-md)', border: '1px solid var(--hairline)',
                  gap: 12,
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <strong style={{ fontSize: 14 }}>{range}</strong>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {mealCount} meal{mealCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(w.id, range)}
                    style={{
                      padding: '7px 14px', fontSize: 13, borderRadius: 'var(--radius-sm)',
                      border: 'none', background: 'var(--pink-primary)',
                      color: 'var(--on-primary)', fontWeight: 600, cursor: 'pointer',
                    }}
                  >Copy →</button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
